'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import {
  LayoutDashboard,
  Activity,
  MessageCircle,
  Trophy,
  ChevronLeft,
  ChevronRight,
  Star,
  Settings,
  Zap,
  GitBranch,
  Globe,
  Target,
  Newspaper,
  Shield,
  History,
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import Icon from '@/components/ui/AppIcon';


interface NavItem {
  id: string;
  label: string;
  icon: React.ElementType;
  href: string;
  badge?: string | number;
}

interface SidebarProps {
  activeRoute?: string;
}

export default function Sidebar({ activeRoute }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);
  const { t } = useLanguage();

  const navItems = [
    { id: 'nav-landing', label: t.nav.home, icon: Globe, href: '/landing' },
    { id: 'nav-dashboard', label: t.nav.dashboard, icon: LayoutDashboard, href: '/live-games-dashboard' },
    { id: 'nav-live', label: t.nav.liveScores, icon: Zap, href: '/live-scores', badge: 'LIVE' },
    { id: 'nav-news', label: t.nav.wcNews, icon: Newspaper, href: '/news' },
    { id: 'nav-standings', label: t.nav.standings, icon: Trophy, href: '/standings' },
    { id: 'nav-teams', label: t.nav.teams, icon: Shield, href: '/teams' },
    { id: 'nav-bracket', label: t.nav.bracket, icon: GitBranch, href: '/bracket' },
    { id: 'nav-predictions', label: t.nav.predictions, icon: Target, href: '/predictions' },
    { id: 'nav-past-games', label: t.nav.pastGames, icon: History, href: '/past-games' },
    { id: 'nav-favorites', label: t.nav.favorites, icon: Star, href: '/favorites' },
    { id: 'nav-detail', label: t.nav.matchDetail, icon: Activity, href: '/game-detail' },
    { id: 'nav-chat', label: t.nav.liveChat, icon: MessageCircle, href: '/live-chat', badge: '128' },
  ];

  const mobileNavItems = [
    { id: 'nav-dashboard', label: t.nav.dashboard, icon: LayoutDashboard, href: '/live-games-dashboard' },
    { id: 'nav-live', label: t.common.live, icon: Zap, href: '/live-scores' },
    { id: 'nav-standings', label: t.nav.standings, icon: Trophy, href: '/standings' },
    { id: 'nav-teams', label: t.nav.teams, icon: Shield, href: '/teams' },
    { id: 'nav-predictions', label: t.nav.predictions, icon: Target, href: '/predictions' },
  ];

  return (
    <>
      {/* Desktop Sidebar */}
      <aside
        className="hidden lg:flex flex-col h-full border-r transition-all duration-300 ease-in-out flex-shrink-0"
        style={{
          width: collapsed ? '64px' : '220px',
          background: 'hsl(222 40% 9%)',
          borderColor: 'hsl(222 25% 15%)',
        }}
      >
        {/* Logo */}
        <div className="flex items-center h-16 px-3 border-b flex-shrink-0" style={{ borderColor: 'hsl(222 25% 15%)' }}>
          <Link href="/landing" className="flex items-center gap-2 min-w-0 hover:opacity-80 transition-opacity">
            <AppLogo size={32} />
            {!collapsed && (
              <span className="font-bold text-base tracking-tight text-white whitespace-nowrap overflow-hidden">
                NOTICOPA
              </span>
            )}
          </Link>
        </div>

        {/* Nav Items */}
        <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto scrollbar-thin">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeRoute === item.href;
            const isLive = item.badge === 'LIVE';
            return (
              <Link
                key={item.id}
                href={item.href}
                title={collapsed ? item.label : undefined}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-150 group relative ${
                  isActive
                    ? 'bg-orange-500/15 text-orange-400' :'text-slate-400 hover:bg-white/5 hover:text-white'
                }`}
              >
                <Icon size={18} className="flex-shrink-0" />
                {!collapsed && (
                  <span className="text-sm font-medium whitespace-nowrap overflow-hidden">{item.label}</span>
                )}
                {!collapsed && item.badge && (
                  <span
                    className="ml-auto text-xs font-semibold px-1.5 py-0.5 rounded-full"
                    style={{
                      background: isLive ? 'hsl(0 84% 60% / 0.15)' : isActive ? 'hsl(25 95% 53% / 0.2)' : 'hsl(222 25% 18%)',
                      color: isLive ? 'hsl(0 84% 70%)' : isActive ? 'hsl(25 95% 65%)' : 'hsl(222 15% 60%)',
                    }}
                  >
                    {isLive ? (
                      <span className="flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-400 live-pulse" />
                        {item.badge}
                      </span>
                    ) : item.badge}
                  </span>
                )}
                {collapsed && item.badge && (
                  <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-orange-500" />
                )}
                {isActive && (
                  <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 rounded-r-full bg-orange-500" />
                )}
              </Link>
            );
          })}
        </nav>

        {/* Bottom */}
        <div className="px-2 pb-4 space-y-1 border-t pt-3 flex-shrink-0" style={{ borderColor: 'hsl(222 25% 15%)' }}>
          <Link
            href="/live-games-dashboard"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-400 hover:bg-white/5 hover:text-white transition-all duration-150"
          >
            <Settings size={18} className="flex-shrink-0" />
            {!collapsed && <span className="text-sm font-medium">{t.nav.settings}</span>}
          </Link>
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-500 hover:bg-white/5 hover:text-slate-300 transition-all duration-150"
          >
            {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            {!collapsed && <span className="text-sm font-medium">{t.nav.collapse}</span>}
          </button>
        </div>
      </aside>

      {/* Mobile Bottom Tab Bar */}
      <nav
        className="lg:hidden fixed bottom-0 left-0 right-0 z-50 flex items-center justify-around h-16 border-t px-2"
        style={{ background: 'hsl(222 40% 9%)', borderColor: 'hsl(222 25% 15%)' }}
      >
        {mobileNavItems.map((item) => {
          const Icon = item.icon;
          const isActive = activeRoute === item.href;
          return (
            <Link
              key={`mobile-${item.id}`}
              href={item.href}
              className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-all duration-150 relative ${
                isActive ? 'text-orange-400' : 'text-slate-500'
              }`}
            >
              <Icon size={20} />
              <span className="text-xs font-medium">{item.label}</span>
            </Link>
          );
        })}
      </nav>
    </>
  );
}